import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { isEmpty, isNumeric } from '../../../js/app/react/validators';
import { determineInputHTMLClass } from '../SimpleSelect/SimpleSelectView';

export const LEFT = 1;
export const CENTER = 2;
export const RIGHT = 3;

function determineInputHTMLStyle(alignment, focused, paddingLeft, paddingRight) {
  switch (alignment) {
    case LEFT:
      if (paddingLeft === null) {
        return {};
      }
      if (focused) {
        return { left: '0px' };
      }
      return {
        left: `${paddingLeft}px`,
      };
    case CENTER:
      return {
        left: '50%',
      };
    case RIGHT:
      if (paddingRight === null) {
        return {};
      }
      if (focused) {
        return { left: '0px' };
      }
      return {
        right: `${paddingRight}px`,
      };
    default:
      throw Error('Unexpected alignment');
  }
}

class Input extends Component {
  constructor() {
    super();
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onInputRef = this.onInputRef.bind(this);
    this.onPostRender = this.onPostRender.bind(this);
    this.state = {
      paddingRight: null,
      paddingLeft: null,
      focused: false,
    };
  }

  componentDidMount() {
    this.onPostRender();
  }

  componentDidUpdate() {
    this.onPostRender();
  }

  onFocus(event) {
    this.setState({
      focused: true,
    });
    this.props.onFocus(event);
  }

  onBlur(event) {
    this.setState({
      focused: false,
    });
    this.props.onBlur(event);
  }

  onInputRef(inputElement) {
    this.inputElement = inputElement;
    const { innerRef } = this.props;

    if (innerRef) {
      innerRef(inputElement);
    }
  }

  onPostRender() {
    const { ADV } = window;
    if (this.inputElement) {
      const newPaddingRight = parseFloat(ADV.util.getCss(this.inputElement, 'paddingRight'));
      if (isNumeric(newPaddingRight)) {
        if (this.state.paddingRight !== newPaddingRight) {
          this.setState({
            paddingRight: newPaddingRight,
          });
        }
      }
      const newPaddingLeft = parseFloat(ADV.util.getCss(this.inputElement, 'paddingLeft'));
      if (isNumeric(newPaddingLeft)) {
        if (this.state.paddingLeft !== newPaddingLeft) {
          this.setState({
            paddingLeft: newPaddingLeft,
          });
        }
      }
    }
  }

  render() {
    const {
      value,
      placeholder,
      type,
      maxLength,
      onChange,
      onKeyDown,
      className,
      placeholderClassName,
      alignment,
      focusedClassName,
      isUnder,
      htmlPlaceholder,
      autoComplete,
      wrapperClassName,
      disabled,
      inputmode,
      readOnly,
      id,
    } = this.props;
    const { focused, paddingRight, paddingLeft } = this.state;

    return (
      <div className={`adv-workflow-input ${wrapperClassName}`}>
        {!htmlPlaceholder && (
          <div
            className={determineInputHTMLClass(
              focused || !isEmpty(value),
              isUnder,
              focusedClassName,
              alignment,
              placeholderClassName,
            )}
            style={determineInputHTMLStyle(
              alignment,
              focused || !isEmpty(value),
              paddingLeft,
              paddingRight,
            )}
            id={id}
          >
            {placeholder}
          </div>
        )}
        <input
          readOnly={readOnly}
          disabled={disabled}
          autoComplete={autoComplete}
          ref={this.onInputRef}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          value={value === null ? '' : value}
          type={type}
          onKeyDown={onKeyDown}
          maxLength={maxLength}
          onChange={onChange}
          className={
            !focused && isEmpty(value)
              ? className
                  .split(' ')
                  .concat('empty')
                  .join(' ')
              : className
          }
          placeholder={htmlPlaceholder}
          pattern={inputmode === 'numeric' ? '[0-9]*' : ''}
          inputMode={inputmode}
          aria-labelledby={id}
        />
      </div>
    );
  }
}
const EMPTY_HANDLER = () => {};
Input.defaultProps = {
  value: '',
  type: 'text',
  isUnder: false,
  onFocus: EMPTY_HANDLER,
  onBlur: EMPTY_HANDLER,
  onKeyDown: EMPTY_HANDLER,
  innerRef: EMPTY_HANDLER,
  className: '',
  placeholderClassName: '',
  focusedClassName: 'adv-workflow-input-placeholder-focused',
  alignment: CENTER,
  htmlPlaceholder: null,
  wrapperClassName: '',
  autoComplete: undefined,
  disabled: false,
  pattern: '',
  inputmode: '',
  readOnly: false,
  id: undefined,
};

Input.propTypes = {
  disabled: PropTypes.bool,
  isUnder: PropTypes.bool,
  className: PropTypes.string,
  placeholderClassName: PropTypes.string,
  focusedClassName: PropTypes.string,
  maxLength: PropTypes.number.isRequired,
  value: PropTypes.number,
  type: PropTypes.oneOf(['text', 'password']),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  innerRef: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  alignment: PropTypes.oneOf([LEFT, CENTER, RIGHT]),
  htmlPlaceholder: PropTypes.string,
  wrapperClassName: PropTypes.string,
  autoComplete: PropTypes.string,
  pattern: PropTypes.string,
  inputmode: PropTypes.string,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
};

export default React.forwardRef((props, ref) => <Input {...props} innerRef={ref} />);
